/**
 * WargameConfig — the per-site configuration contract for miniatures wargame
 * sites (Battlestrike/Titanvell and future settings sharing its 3D tabletop
 * simulator + character-builder template).
 *
 * Deliberately a SEPARATE contract from `@gamecodex/world-config`'s
 * `WorldConfig`, not an extension of it. `WorldConfig` is RPG-book-shaped
 * throughout (encyclopedia facets, book-entitlement gating, crowdfunding
 * `homeCampaign`) — a wargame site has none of those concerns, and bolting
 * `unlockBookIds`/`fallbackUnlockBookId`/etc. onto a site with no books would
 * be actively wrong, not just unused. Where the SHAPE of a concern is
 * identical (locales, message catalogs, SEO), the types are copied verbatim
 * rather than imported, to keep this package dependency-free and free to
 * diverge later without touching the RPG-book side.
 *
 * The active config is a process singleton, registered once at bootstrap
 * (`setActiveWargameConfig` in the app's `main.tsx`). Shared `@gamecodex/
 * wargame-ui` modules read it lazily inside their functions via
 * {@link getActiveWargameConfig} — never at module load — so import order is
 * irrelevant, mirroring the WorldConfig pattern exactly.
 */

// ── i18n: locales + content catalog (same shape as WorldConfig, copied not imported) ──

/** A locale this wargame site supports. `code` is a BCP-47 tag used in URLs. */
export interface WargameLocaleDef {
  code: string
  label: string
  native: string
  dir: 'ltr' | 'rtl'
}

export interface WargameLocalesConfig {
  default: string
  supported: WargameLocaleDef[]
}

/** A flat, dotted-key message catalog for ONE locale (`home.heroTitle`, etc.). */
export type WargameMessageCatalog = Record<string, string>
export type WargameContent = Record<string, WargameMessageCatalog>

// ── Branding ─────────────────────────────────────────────────────────────────

/** Per-site visual identity. Simpler than WorldConfig's — no parchment/blood-splatter
 *  texture slots, since a wargame site's visual language isn't gothic-parchment by default. */
export interface WargameBranding {
  images: {
    logo: string
    ogImage: string
    favicon: string
  }
  /** Set true when `images.logo` is a full wordmark (site name baked into the
   *  image, e.g. a stylized "TITANVELL" logotype) — TopNav then renders the
   *  image alone instead of an icon + separate text label, and sizes it by
   *  height rather than forcing a square box. Defaults to false (icon + text). */
  logoIsWordmark?: boolean
  fonts: {
    display: string
    heading: string
    body: string
    stylesheetUrl: string
  }
  /** Pluggable animated landing background, keyed by a registered variant — same
   *  registry mechanism as WorldConfig's `background`, resolved by the wargame shell. */
  background: {
    variant: string
    params?: Record<string, unknown>
  }
  /** Primary/accent hex colors for UI chrome that isn't texture-driven (buttons,
   *  HUD panels in the 3D simulator, faction badges). */
  colors: {
    primary: string
    accent: string
  }
}

// ── Factions / character builder ─────────────────────────────────────────────

/** One playable faction, as surfaced by the Character/Titan Builder. Data here is
 *  DISPLAY metadata only (name, blurb, color) — the actual stats/dice/profiles are
 *  fetched live from the rules system's `system_entity` data (or the sourcebook),
 *  never hardcoded here, so the builder works for any setting overlaid on this
 *  template without a code change. */
export interface WargameFactionOption {
  /** Stable slug matching the system's own faction/type key. */
  id: string
  label: string
  blurb?: string
  /** Accent color for faction-tinted UI (badges, tokens, dice art). */
  color?: string
}

// ── Simulator ────────────────────────────────────────────────────────────────

export interface WargameSimulatorConfig {
  /** Default tabletop dimensions in inches — the simulator's coordinate space
   *  is always measured in inches, never grid cells (see ADR: non-grid movement). */
  boardSize: { width: number; height: number }
  /** Available die faces this rules system uses, for the dice tray UI. */
  diceFaces: number[]
}

export interface WargameConfig {
  /** Stable site slug (e.g. `titanvell`). */
  wargameId: string
  /** Display name (e.g. `Titanvell`). */
  wargameName: string
  /** Name for the first-party content this site authors, used in legal copy —
   *  same three-case reasoning as `WorldConfig.legalContentName`. Defaults to
   *  `wargameName` when unset (fully-original IP, the common case here). */
  legalContentName?: string
  /** Primary domain (e.g. `titanvell.com`). */
  domain: string

  locales: WargameLocalesConfig
  content: WargameContent
  branding: WargameBranding

  /** The rules system this site plays — the api `game_system.code`
   *  (e.g. `battlestrike`). Exactly one per wargame site; unlike WorldConfig's
   *  `gameSystems[]` facet list, a wargame site is not multi-system. */
  rulesSystemId: string
  /** The sourcebook rendered on the How to Play page — the api `pub_book.id`. */
  sourcebookId: string

  /** Factions surfaced by the Character/Titan Builder. */
  factions: WargameFactionOption[]

  /** Home page hero/intro copy. */
  homeIntro: {
    eyebrow: string
    title: string
    body: string
    ctaLabel: string
    /** Optional decorative character art bled off the left edge of the hero
     *  section, behind the copy — purely atmospheric, not required. */
    heroImage?: string
    /** Optional second decorative character art bled off the RIGHT edge,
     *  for a "faceoff" look against `heroImage`. Hidden below the `lg`
     *  breakpoint (left image alone is enough on narrower screens — two
     *  side portraits crowd the centered copy on tablet-width viewports). */
    heroImageRight?: string
  }

  /** 3D tabletop simulator defaults. */
  simulator: WargameSimulatorConfig

  /** Physical-merchandise store (terrain kits, dice sets, miniature kits, …).
   *  Optional — omit entirely for a site with no store yet. This is the
   *  DISPLAY catalog (name/price/image/category); the live Atlas SKU catalog
   *  (`GET /api/commerce/catalog?game_system=…`) is the source of truth for
   *  price/availability/ownership once Atlas has real SKUs — items here are
   *  matched to it by `skuCode` and used as the offline/fallback display data
   *  (and as the pre-launch "coming soon" catalog before Atlas has the SKUs). */
  store?: WargameStoreConfig

  /** Crowdfunding CTA (Kickstarter prelaunch/live page). Optional — omit
   *  entirely until a real campaign URL exists; a fake/placeholder URL should
   *  never be set just to have something to show. `label` defaults to
   *  "Notify Me on Launch" while `status: 'prelaunch'` and "Back Us on
   *  Kickstarter" while `status: 'live'`. Shown in the persistent nav AND the
   *  home hero — "put it where it converts" per the ask. */
  kickstarter?: {
    url: string
    status: 'prelaunch' | 'live'
    label?: string
  }

  // ── SEO / GEO defaults for the shared <Seo> head — same shape as WorldConfig ──
  seo: {
    defaultDescription: string
    tagline: string
    defaultOgImage: string
  }
}

// ── Physical store ───────────────────────────────────────────────────────────

export interface WargameStoreProduct {
  /** Stable slug, matched against the Atlas SKU catalog's `skuCode`. */
  skuCode: string
  name: string
  description?: string
  /** Display price in whole currency units (e.g. 49.99) — Atlas's live catalog
   *  price (cents) wins once available; this is the pre-launch/fallback price. */
  price: number
  image?: string
}

export interface WargameStoreCategory {
  id: string
  label: string
  products: WargameStoreProduct[]
}

export interface WargameStoreConfig {
  enabled: boolean
  categories: WargameStoreCategory[]
}

let active: WargameConfig | null = null

/** Register the active wargame config. Call once at app bootstrap (and test setup). */
export function setActiveWargameConfig(config: WargameConfig): void {
  active = config
}

/** The active wargame config. Throws if bootstrap never registered one. */
export function getActiveWargameConfig(): WargameConfig {
  if (!active) {
    throw new Error(
      'WargameConfig not initialized — call setActiveWargameConfig() at app bootstrap (main.tsx) or in test setup.',
    )
  }
  return active
}

/** Whether a wargame config has been registered (handy in tests/guards). */
export function hasActiveWargameConfig(): boolean {
  return active !== null
}
